/**
 * Which of the Add Exercise modal's three commit buttons is primary, secondary
 * or disabled. Exactly one is ever primary: the next logical step.
 *
 * rowValid: the set entry row has Sets and Reps (or AMRAP).
 * rowEmpty: none of the set entry fields has a value.
 * exerciseHasSets: the exercise being built has at least one committed set.
 * queueHasExercises: the workout queue holds at least one exercise.
 *
 * @param {{ rowValid: boolean, rowEmpty: boolean, exerciseHasSets: boolean, queueHasExercises: boolean, editingSet: boolean, queueEnabled: boolean }} state
 * @returns {{ addSet: "primary" | "secondary" | "disabled", addToWorkout: "primary" | "secondary" | "disabled", save: "primary" | "secondary" | "disabled" }}
 */
export function getButtonStates({
  rowValid,
  rowEmpty,
  exerciseHasSets,
  queueHasExercises,
  editingSet,
  queueEnabled,
}) {
  if (editingSet) {
    return {
      addSet: "disabled",
      addToWorkout: "secondary",
      save: "secondary",
    };
  }

  // Editing an existing exercise or a flow without a queue: the footer button
  // is the only save, so it follows the same "next logical step" rule.
  if (!queueEnabled) {
    return {
      addSet: rowValid ? "primary" : "disabled",
      addToWorkout: "disabled",
      save: exerciseHasSets
        ? rowValid
          ? "secondary"
          : "primary"
        : rowValid
          ? "secondary"
          : "disabled",
    };
  }

  if (rowValid) {
    return {
      addSet: "primary",
      addToWorkout: "secondary",
      save: "secondary",
    };
  }

  if (rowEmpty) {
    if (exerciseHasSets) {
      return {
        addSet: "disabled",
        addToWorkout: "primary",
        save: "secondary",
      };
    }
    if (queueHasExercises) {
      return { addSet: "disabled", addToWorkout: "disabled", save: "primary" };
    }
    return { addSet: "disabled", addToWorkout: "disabled", save: "disabled" };
  }

  // Half-filled row: nothing can be committed from it.
  return {
    addSet: "disabled",
    addToWorkout: exerciseHasSets ? "primary" : "disabled",
    save: exerciseHasSets || queueHasExercises ? "secondary" : "disabled",
  };
}

/**
 * What Enter does in a set entry field.
 *
 * @param {{ rowValid: boolean, rowEmpty: boolean, exerciseHasSets: boolean, queueEnabled: boolean }} state
 * @returns {"addSet" | "addToWorkout" | null}
 */
export function getEnterAction({
  rowValid,
  rowEmpty,
  exerciseHasSets,
  queueEnabled,
}) {
  if (rowValid) return "addSet";
  if (rowEmpty && exerciseHasSets && queueEnabled) return "addToWorkout";
  return null;
}

const isBlank = (value) =>
  value === undefined ||
  value === null ||
  value === "" ||
  value === 0 ||
  value === "0" ||
  Number.isNaN(value);

/**
 * True when none of the set entry fields has a value. Rest time is not one of
 * the four fields, so it does not count. The load inputs show 0 by default.
 *
 * @param {Record<string, unknown>} newSet standard set row state
 * @param {Record<string, unknown>} parametersForm custom-parameter row state
 * @param {{ parameters?: unknown[] } | undefined} selectedExercise
 */
export function isSetRowEmpty(newSet, parametersForm, selectedExercise) {
  if (selectedExercise?.parameters) {
    return Object.entries(parametersForm || {}).every(
      ([key, value]) => key === "restTime" || isBlank(value)
    );
  }
  const fields = [
    "sets",
    "reps",
    "repsTwo",
    "repsThree",
    "rpe",
    "load",
    "percentRepMax",
    "loadDrop",
  ];
  return fields.every((field) => isBlank(newSet?.[field]));
}
